import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Atom, Cpu, Save, RefreshCcw, Zap } from "lucide-react";

const AUTH_HDR = () => ({ Authorization: `Bearer ${localStorage.getItem("ndaku_token") || ""}` });

const ENGINE_OPTS = ["classic", "quantum", "best"];

export default function EngineAdminPage() {
  const [cfg, setCfg] = useState(null);
  const [perf, setPerf] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(false);
  // Iter 145bi — Per-(league, market) drill-down + auto-tune controls.
  const [drill, setDrill] = useState(null);
  const [drillDays, setDrillDays] = useState(30);
  const [drillMinSamples, setDrillMinSamples] = useState(5);
  const [drillWinner, setDrillWinner] = useState("all"); // all | quantum | classic
  // Iter 145bj — ROI-weighted sort option.
  const [drillSort, setDrillSort] = useState("hit_rate"); // hit_rate | roi
  const [autoTune, setAutoTune] = useState(null);
  const [tuning, setTuning] = useState(false);

  const load = async () => {
    try {
      const [c, p, cov, dr, at] = await Promise.all([
        axios.get(`${API}/admin/engines/config`, { headers: AUTH_HDR() }),
        axios.get(`${API}/admin/engines/performance?days=60`, { headers: AUTH_HDR() }),
        axios.get(`${API}/admin/engines/quantum/coverage`, { headers: AUTH_HDR() }).catch(() => null),
        axios.get(`${API}/admin/engines/performance/by-league`, {
          params: { days: drillDays, min_samples: drillMinSamples, sort: drillSort },
          headers: AUTH_HDR(),
        }).catch(() => null),
        axios.get(`${API}/admin/engines/auto-tune/status`, { headers: AUTH_HDR() }).catch(() => null),
      ]);
      setCfg(c.data);
      setPerf(p.data);
      if (cov?.data) setCoverage(cov.data);
      if (dr?.data) setDrill(dr.data);
      if (at?.data) setAutoTune(at.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load engine config");
    }
  };

  useEffect(() => { load(); /* initial load */ }, []);

  const reloadDrill = async (days = drillDays, min = drillMinSamples, sort = drillSort) => {
    try {
      const r = await axios.get(`${API}/admin/engines/performance/by-league`, {
        params: { days, min_samples: min, sort },
        headers: AUTH_HDR(),
      });
      setDrill(r.data);
    } catch (e) {
      toast.error("Failed to refresh drill-down");
    }
  };

  const runAutoTune = async () => {
    setTuning(true);
    try {
      const r = await axios.post(`${API}/admin/engines/auto-tune/run`, null, { headers: AUTH_HDR() });
      setAutoTune({ current_sample_rate: r.data.new_rate, last_tune: r.data });
      toast.success(`Auto-tune: ${r.data.decision} → ${(r.data.new_rate * 100).toFixed(0)}%`);
      load();  // refresh cfg to show new rate
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-tune failed");
    } finally {
      setTuning(false);
    }
  };

  const toggle = async (engine, value) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/engines/toggle`,
        { [engine]: value }, { headers: AUTH_HDR() });
      setCfg(r.data);
      toast.success(`${engine} engine ${value ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusy(false); }
  };

  const setRouting = async (market, eng) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/engines/headline-routing`,
        { routing: { [market]: eng } }, { headers: AUTH_HDR() });
      setCfg(r.data);
      toast.success(`${market} → ${eng}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Routing update failed");
    } finally { setBusy(false); }
  };

  const runQuantumNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/engines/quantum/batch-run?force=true`,
        null, { headers: AUTH_HDR() });
      // Iter 145bf — fire-and-forget: server returns {started:true} quickly.
      // We poll the status endpoint for the actual outcome.
      if (r.data?.started) {
        toast.success("Quantum batch-run launched — polling for results…");
        // Poll status for up to 90s
        let polls = 0;
        const startedAt = r.data.started_at_iso;
        const poll = async () => {
          polls += 1;
          try {
            const s = await axios.get(`${API}/admin/engines/quantum/batch-run/status?limit=3`, { headers: AUTH_HDR() });
            const recent = (s.data?.recent_runs || []).find(x => x.started_at_iso === startedAt);
            if (recent) {
              if (recent.error) {
                toast.error(`Quantum failed: ${recent.error}`);
              } else {
                toast.success(`Quantum: processed ${recent.processed} / ${recent.n_matches} (${recent.duration_s}s)`);
              }
              load();
              setBusy(false);
              return;
            }
          } catch { /* keep polling */ }
          if (polls < 30) {
            setTimeout(poll, 3000);
          } else {
            toast("Still running in background. Refresh in a minute to see updated performance.");
            setBusy(false);
          }
        };
        setTimeout(poll, 3000);
        return; // don't fall through to setBusy(false) below
      } else {
        // Legacy synchronous response (preview pre-145bf)
        toast.success(`Quantum: processed ${r.data.processed} / ${r.data.n_matches} (${r.data.duration_s}s)`);
        load();
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
      setBusy(false);
    }
  };

  if (!cfg) {
    return (
      <main className="min-h-screen bg-background text-foreground p-6">
        <p className="text-muted-foreground">Loading engine controls…</p>
      </main>
    );
  }

  const engines = cfg.engines || {};
  const routing = cfg.headline_routing || {};

  return (
    <main className="min-h-screen bg-background text-foreground p-4 sm:p-6" data-testid="engine-admin-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Engine Control Center</h1>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-border hover:bg-foreground hover:text-background transition"
              data-testid="engine-refresh-btn">
              <RefreshCcw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button onClick={runQuantumNow} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-cyan-500 text-background hover:bg-cyan-400 transition"
              data-testid="engine-run-quantum-btn">
              <Zap className="w-3.5 h-3.5" />
              Run Quantum Now
            </button>
          </div>
        </header>

        {/* Engine toggles */}
        <section className="rounded-2xl border border-border bg-card/30 p-5 space-y-4">
          <h2 className="font-bold text-base">Engine Activation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ToggleCard
              icon={Cpu}
              label="Classic Engine"
              sub="Legacy NDAKUBET algorithm (ELO + Bivariate Poisson + market consensus)"
              enabled={engines.classic !== false}
              onToggle={(v) => toggle("classic", v)}
              testid="toggle-classic"
            />
            <ToggleCard
              icon={Atom}
              label="Quantum Ensemble Engine"
              sub="Dixon-Coles + Bivariate Poisson + Monte Carlo + Bayesian + auto-learning"
              enabled={engines.quantum !== false}
              onToggle={(v) => toggle("quantum", v)}
              testid="toggle-quantum"
              accent="cyan"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Parallel-run rate: <span className="text-foreground font-semibold">{Math.round((cfg.parallel_sample_rate || 1) * 100)}%</span> ·
            Updated: {cfg.updated_at ? new Date(cfg.updated_at).toLocaleString() : "never"}
            {cfg.updated_by && ` by ${cfg.updated_by}`}
          </p>
        </section>

        {/* Iter 145bg — Quantum coverage panel: makes it obvious the
            automatic cron is producing predictions without admin
            having to click the manual run button. */}
        {coverage && (
          <section className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-5 space-y-3" data-testid="quantum-coverage">
            <div className="flex items-center gap-2">
              <Atom className="w-4 h-4 text-cyan-400" />
              <h2 className="font-bold text-base">Automatic Quantum Coverage</h2>
              <span className="ml-auto text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-300/80">
                Cron: every 10 min
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <CoverageStat
                label="Today · Quantum"
                value={`${coverage.today?.quantum_covered ?? 0} / ${coverage.today?.total_fixtures ?? 0}`}
                pct={coverage.today?.quantum_pct ?? 0}
                testid="cov-today-quantum"
              />
              <CoverageStat
                label="Today · Classic"
                value={`${coverage.today?.classic_covered ?? 0} / ${coverage.today?.total_fixtures ?? 0}`}
                pct={coverage.today?.classic_pct ?? 0}
                accent="amber"
                testid="cov-today-classic"
              />
              <CoverageStat
                label="Tomorrow · Quantum"
                value={`${coverage.tomorrow?.quantum_covered ?? 0} / ${coverage.tomorrow?.total_fixtures ?? 0}`}
                pct={coverage.tomorrow?.quantum_pct ?? 0}
                testid="cov-tomorrow-quantum"
              />
              <CoverageStat
                label="Tomorrow · Classic"
                value={`${coverage.tomorrow?.classic_covered ?? 0} / ${coverage.tomorrow?.total_fixtures ?? 0}`}
                pct={coverage.tomorrow?.classic_pct ?? 0}
                accent="amber"
                testid="cov-tomorrow-classic"
              />
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>
                Last Quantum prediction generated:{" "}
                <span className="text-foreground font-semibold">
                  {coverage.last_quantum_prediction_at
                    ? new Date(coverage.last_quantum_prediction_at).toLocaleString()
                    : "never"}
                </span>
              </p>
              {coverage.last_batch_run && (
                <p>
                  Last cron batch:{" "}
                  <span className="text-foreground font-semibold">
                    {new Date(coverage.last_batch_run.finished_at_iso).toLocaleTimeString()}
                  </span>{" "}
                  — processed {coverage.last_batch_run.processed} / {coverage.last_batch_run.n_matches}{" "}
                  ({coverage.last_batch_run.duration_s}s)
                  {coverage.last_batch_run.error && (
                    <span className="text-rose-300"> · ERROR: {coverage.last_batch_run.error}</span>
                  )}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Per-market routing */}
        <section className="rounded-2xl border border-border bg-card/30 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-base">Headline Engine Per Market</h2>
            <span className="text-[11px] text-muted-foreground">classic · quantum · best</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(routing).map(([mt, eng]) => (
              <div key={mt} className="flex items-center justify-between rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                <span className="text-sm font-medium">{mt === "default" ? "(default)" : mt}</span>
                <div className="flex gap-1">
                  {ENGINE_OPTS.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setRouting(mt, opt)}
                      disabled={busy || eng === opt}
                      data-testid={`route-${mt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${opt}`}
                      className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full transition ${
                        eng === opt
                          ? "bg-foreground text-background"
                          : "bg-card border border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Performance table */}
        {perf && (
          <section className="rounded-2xl border border-border bg-card/30 p-5" data-testid="engine-performance-table">
            <div className="flex items-center gap-2 mb-3">
              <Save className="w-4 h-4 text-amber-400" />
              <h2 className="font-bold text-base">Engine Performance (60 days)</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="text-left px-2 py-2">Market</th>
                    <th className="text-right px-2 py-2">Classic Hit</th>
                    <th className="text-right px-2 py-2">Classic ROI</th>
                    <th className="text-right px-2 py-2">Quantum Hit</th>
                    <th className="text-right px-2 py-2">Quantum ROI</th>
                    <th className="text-center px-2 py-2">Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {(perf.markets || []).map((r) => (
                    <tr key={r.market_type} className="border-t border-border/30">
                      <td className="px-2 py-2 font-medium">{r.market_type}</td>
                      <td className="px-2 py-2 text-right">{r.classic?.hit_rate != null ? `${(r.classic.hit_rate * 100).toFixed(1)}% (${r.classic.samples})` : "—"}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{r.classic?.est_roi != null ? `${(r.classic.est_roi * 100).toFixed(1)}%` : "—"}</td>
                      <td className="px-2 py-2 text-right">{r.quantum?.hit_rate != null ? `${(r.quantum.hit_rate * 100).toFixed(1)}% (${r.quantum.samples})` : "—"}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{r.quantum?.est_roi != null ? `${(r.quantum.est_roi * 100).toFixed(1)}%` : "—"}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${
                          r.winner === "quantum" ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                          : r.winner === "classic" ? "bg-foreground/10 text-foreground/80 border border-border"
                          : "bg-muted/30 text-muted-foreground border border-border/40"
                        }`}>
                          {r.winner}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Iter 145bi — Per-(league, market) drill-down */}
        {drill && (
          <section className="rounded-2xl border border-border bg-card/30 p-5" data-testid="engine-drilldown-table">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" />
                <h2 className="font-bold text-base">Per-League · Per-Market Drill-down</h2>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {drill.row_count} rows
                </span>
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                {[7, 30, 60, 180].map((d) => (
                  <button
                    key={d}
                    onClick={() => { setDrillDays(d); reloadDrill(d, drillMinSamples, drillSort); }}
                    data-testid={`drill-days-${d}`}
                    className={`px-2.5 py-1 rounded-full border ${
                      drillDays === d
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >{d}d</button>
                ))}
                <span className="mx-2 text-muted-foreground">·</span>
                {[3, 5, 10, 20].map((s) => (
                  <button
                    key={s}
                    onClick={() => { setDrillMinSamples(s); reloadDrill(drillDays, s, drillSort); }}
                    data-testid={`drill-samples-${s}`}
                    className={`px-2.5 py-1 rounded-full border ${
                      drillMinSamples === s
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >≥{s}</button>
                ))}
                <span className="mx-2 text-muted-foreground">·</span>
                {/* Iter 145bj — Sort toggle: hit-rate vs ROI */}
                {[
                  { k: "hit_rate", l: "Hit" },
                  { k: "roi", l: "ROI" },
                ].map(({ k, l }) => (
                  <button
                    key={k}
                    onClick={() => { setDrillSort(k); reloadDrill(drillDays, drillMinSamples, k); }}
                    data-testid={`drill-sort-${k}`}
                    className={`px-2.5 py-1 rounded-full border uppercase tracking-wider ${
                      drillSort === k
                        ? "bg-emerald-500 text-emerald-950 border-emerald-500"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >Sort: {l}</button>
                ))}
                <span className="mx-2 text-muted-foreground">·</span>
                {["all", "quantum", "classic"].map((w) => (
                  <button
                    key={w}
                    onClick={() => setDrillWinner(w)}
                    data-testid={`drill-winner-${w}`}
                    className={`px-2.5 py-1 rounded-full border uppercase tracking-wider ${
                      drillWinner === w
                        ? (w === "quantum" ? "bg-cyan-500 text-cyan-950 border-cyan-500"
                           : w === "classic" ? "bg-amber-500 text-amber-950 border-amber-500"
                           : "bg-foreground text-background border-foreground")
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >{w}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card/95 backdrop-blur">
                  <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="text-left px-2 py-2">League</th>
                    <th className="text-left px-2 py-2">Market</th>
                    <th className="text-right px-2 py-2">Classic Hit</th>
                    <th className="text-right px-2 py-2">Classic ROI</th>
                    <th className="text-right px-2 py-2">Quantum Hit</th>
                    <th className="text-right px-2 py-2">Quantum ROI</th>
                    <th className="text-right px-2 py-2">
                      Δ {drillSort === "roi" ? "ROI" : "Hit"} pp
                    </th>
                    <th className="text-center px-2 py-2">Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {(drill.rows || [])
                    .filter((r) => {
                      const activeWinner = drillSort === "roi" ? r.roi_winner : r.winner;
                      return drillWinner === "all" || activeWinner === drillWinner;
                    })
                    .slice(0, 250)
                    .map((r, idx) => {
                      const activeDelta = drillSort === "roi" ? r.delta_roi_pp : r.delta_pp;
                      const activeWinner = drillSort === "roi" ? r.roi_winner : r.winner;
                      return (
                      <tr key={`${r.league_name}-${r.market_type}-${idx}`} className="border-t border-border/30">
                        <td className="px-2 py-1.5 truncate max-w-[200px]">
                          <span className="font-medium">{r.league_name}</span>
                          {r.country && <span className="text-muted-foreground"> · {r.country}</span>}
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground">{r.market_type}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {r.classic.hit_rate != null
                            ? `${r.classic.hit_rate}% (${r.classic.samples})`
                            : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className={`px-2 py-1.5 text-right tabular-nums ${
                          r.classic.roi_pct == null ? "text-muted-foreground/50"
                          : r.classic.roi_pct > 0 ? "text-emerald-300"
                          : "text-rose-300/80"}`}
                        >
                          {r.classic.roi_pct == null ? "—" : `${r.classic.roi_pct > 0 ? "+" : ""}${r.classic.roi_pct}%`}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {r.quantum.hit_rate != null
                            ? `${r.quantum.hit_rate}% (${r.quantum.samples})`
                            : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className={`px-2 py-1.5 text-right tabular-nums ${
                          r.quantum.roi_pct == null ? "text-muted-foreground/50"
                          : r.quantum.roi_pct > 0 ? "text-emerald-300"
                          : "text-rose-300/80"}`}
                        >
                          {r.quantum.roi_pct == null ? "—" : `${r.quantum.roi_pct > 0 ? "+" : ""}${r.quantum.roi_pct}%`}
                        </td>
                        <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${
                          activeDelta == null ? "text-muted-foreground/50"
                          : activeDelta > 0 ? "text-cyan-300"
                          : activeDelta < 0 ? "text-amber-300" : "text-muted-foreground"}`}
                        >
                          {activeDelta == null ? "—" : `${activeDelta > 0 ? "+" : ""}${activeDelta}`}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${
                            activeWinner === "quantum" ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
                            : activeWinner === "classic" ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                            : "border-border/40 bg-muted/20 text-muted-foreground"}`}>
                            {activeWinner === "quantum_only" ? "Q-only"
                              : activeWinner === "classic_only" ? "C-only"
                              : activeWinner}
                          </span>
                        </td>
                      </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {drillSort === "roi"
                ? "Rows ranked by largest absolute ROI gap (Quantum − Classic). ROI = hit-rate × avg-implied-odds − 1 (flat-stake, model-implied)."
                : "Rows ranked by largest absolute hit-rate gap (Quantum − Classic)."}{" "}
              Showing first 250.
            </p>
          </section>
        )}

        {/* Iter 145bi — Auto-tune sample rate */}
        {autoTune && (
          <section className="rounded-2xl border border-border bg-card/30 p-5 space-y-3" data-testid="engine-auto-tune">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" />
                <h2 className="font-bold text-base">Auto-tune Parallel Sample Rate</h2>
              </div>
              <button
                onClick={runAutoTune}
                disabled={tuning}
                data-testid="auto-tune-run"
                className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
              >
                <RefreshCcw className={`w-3.5 h-3.5 inline mr-1.5 ${tuning ? "animate-spin" : ""}`} />
                {tuning ? "Running…" : "Run now"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <CoverageStat
                label="Current rate"
                value={`${Math.round((autoTune.current_sample_rate ?? 1) * 100)}%`}
                pct={(autoTune.current_sample_rate ?? 1) * 100}
                testid="auto-tune-rate"
              />
              <CoverageStat
                label="Last decision"
                value={autoTune.last_tune?.decision || "—"}
                pct={autoTune.last_tune?.applied ? 100 : 0}
                accent={autoTune.last_tune?.decision === "scale-up" ? "cyan" : "amber"}
                testid="auto-tune-decision"
              />
              <CoverageStat
                label="Quantum edge"
                value={autoTune.last_tune?.quantum_edge_pp != null
                  ? `${autoTune.last_tune.quantum_edge_pp > 0 ? "+" : ""}${autoTune.last_tune.quantum_edge_pp} pp`
                  : "n/a"}
                pct={Math.max(0, Math.min(100, 50 + (autoTune.last_tune?.quantum_edge_pp ?? 0) * 5))}
                testid="auto-tune-edge"
              />
              <CoverageStat
                label="Quantum samples (14d)"
                value={`${autoTune.last_tune?.quantum?.samples ?? 0}`}
                pct={Math.min(100, ((autoTune.last_tune?.quantum?.samples ?? 0) / 500) * 100)}
                testid="auto-tune-samples"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Cron: every 6 h · last run {autoTune.last_tune?.ran_at
                ? new Date(autoTune.last_tune.ran_at).toLocaleString()
                : "never"}.{" "}
              Rate scales up when Quantum beats Classic on win-rate over the last 14 days and scales down when it underperforms.
            </p>
          </section>
        )}
      </div>
    </main>
  );
}

const CoverageStat = ({ label, value, pct, accent = "cyan", testid }) => {
  const clampedPct = Math.max(0, Math.min(100, Number(pct) || 0));
  const barColor = accent === "amber" ? "bg-amber-400" : "bg-cyan-400";
  const txtColor = accent === "amber" ? "text-amber-300" : "text-cyan-300";
  return (
    <div className="rounded-lg border border-border/40 bg-background/40 px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold tabular-nums">{value}</div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-muted/40 overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${clampedPct}%` }}
        />
      </div>
      <div className={`mt-1 text-[11px] font-bold ${txtColor}`}>{clampedPct.toFixed(1)}%</div>
    </div>
  );
};

const ToggleCard = ({ icon: Icon, label, sub, enabled, onToggle, testid, accent = "amber" }) => (
  <div className={`rounded-xl border ${enabled ? `border-${accent}-500/40 bg-${accent}-500/5` : "border-border bg-card/30"} p-4`}>
    <div className="flex items-start gap-3">
      <Icon className={`w-5 h-5 ${enabled ? `text-${accent}-400` : "text-muted-foreground"} mt-0.5`} />
      <div className="flex-1">
        <div className="font-semibold">{label}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
      </div>
      <button
        onClick={() => onToggle(!enabled)}
        data-testid={testid}
        className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-muted"}`}
        aria-label="toggle"
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : ""}`}
        />
      </button>
    </div>
  </div>
);
